import { ChevronRightRounded, MoreHorizRounded } from '@mui/icons-material';
import { Breadcrumbs, ButtonBase, IconButton, Menu, MenuItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useId, useLayoutEffect, useRef, useState, type FocusEvent } from 'react';
import { createFocusRingStyles } from '../interaction';
import { designTokens } from '../tokens';

export type HierarchyBreadcrumbItem = { id: string; label: string };

export type HierarchyBreadcrumbsProps = {
  items: readonly HierarchyBreadcrumbItem[];
  ariaLabel: string;
  overflowLabel: string;
  onNavigate: (id: string) => void;
};

/** 마지막 항목은 현재 위치이며, 긴 경로의 중간 항목은 키보드로 조작하는 메뉴에 모읍니다. */
export function HierarchyBreadcrumbs({ items, ariaLabel, overflowLabel, onNavigate }: HierarchyBreadcrumbsProps) {
  const compact = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const triggerId = useId();
  const currentRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const focusedElement = useRef<HTMLElement | null>(null);
  const collapsed = items.length > (compact ? 2 : 4);
  const hiddenItems = collapsed ? items.slice(1, -1) : [];
  const current = items.at(-1);
  useLayoutEffect(() => {
    // 펼쳐진 경로에서는 제거된 trigger를 anchor로 남겨 다음 축소 때 메뉴가 다시 열리지 않게 합니다.
    if (anchor && !collapsed) setAnchor(null);
    if ((anchor && !collapsed) || (focusedElement.current && !focusedElement.current.isConnected)) {
      (collapsed ? triggerRef.current : currentRef.current)?.focus({ preventScroll: true });
    }
  }, [anchor, collapsed, items]);
  if (!current) return null;

  const closeMenu = () => {
    anchor?.focus({ preventScroll: true });
    setAnchor(null);
  };
  const ancestor = (item: HierarchyBreadcrumbItem) => <ButtonBase
    key={item.id} data-breadcrumb-id={item.id} onClick={() => onNavigate(item.id)}
    sx={{ ...designTokens.typography.ui, minWidth: 0, minHeight: designTokens.button.sizes.small.height, textAlign: 'left', overflowWrap: 'anywhere', color: designTokens.color.secondaryText, borderRadius: designTokens.shape.sm, px: `${designTokens.space.xxs}px`, '&:hover': { color: designTokens.color.actionDark, textDecoration: 'underline' }, '&:focus-visible': createFocusRingStyles({ inset: true }), '@media (pointer: coarse)': { minHeight: designTokens.layout.touchTargetMinSize } }}
  >{item.label}</ButtonBase>;

  return <>
    <Breadcrumbs
      aria-label={ariaLabel} maxItems={Number.MAX_SAFE_INTEGER}
      onFocusCapture={(event) => { focusedElement.current = event.target; }}
      onBlurCapture={() => { focusedElement.current = null; }}
      separator={<ChevronRightRounded aria-hidden fontSize="small" />}
      sx={{ minWidth: 0, color: designTokens.color.secondaryText, '& .MuiBreadcrumbs-ol': { rowGap: `${designTokens.space.xxs}px` }, '& .MuiBreadcrumbs-li': { minWidth: 0, maxWidth: '100%' }, '& .MuiBreadcrumbs-separator': { mx: `${designTokens.space.xxs}px` } }}
    >
      {collapsed ? ancestor(items[0]) : items.slice(0, -1).map(ancestor)}
      {collapsed && <IconButton
        ref={triggerRef} id={triggerId} size="small" data-breadcrumb-overflow aria-label={overflowLabel}
        aria-haspopup="menu" aria-expanded={Boolean(anchor)} aria-controls={anchor ? menuId : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
      ><MoreHorizRounded /></IconButton>}
      <Typography ref={currentRef} component="span" tabIndex={-1} variant="body2" aria-current="location" sx={{ display: 'block', overflowWrap: 'anywhere', color: designTokens.color.primaryText, fontWeight: 500, px: `${designTokens.space.xxs}px` }}>
        {current.label}
      </Typography>
    </Breadcrumbs>
    <Menu
      id={menuId} anchorEl={anchor} open={Boolean(anchor) && hiddenItems.length > 0} onClose={closeMenu}
      disableRestoreFocus
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: -designTokens.layout.menuAnchorGap, horizontal: 'left' }}
      slotProps={{ list: { 'aria-labelledby': triggerId, onFocusCapture: (event: FocusEvent<HTMLUListElement>) => { focusedElement.current = event.target; }, onBlurCapture: () => { focusedElement.current = null; } } }}
    >
      {hiddenItems.map((item) => <MenuItem key={item.id} data-breadcrumb-id={item.id} onClick={() => { closeMenu(); onNavigate(item.id); }}>{item.label}</MenuItem>)}
    </Menu>
  </>;
}
